import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { AuthShell } from "@/components/AuthShell";
import { Field } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";

export default function Activate() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const nav = useNavigate();
  const { applyAuth } = useAuth();
  const [inv, setInv] = useState(null);
  const [f, setF] = useState({ name: "", business_name: "", password: "", confirm: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get(`/access/invite/${token}`).then((r) => { setInv(r.data); setF((x) => ({ ...x, name: r.data.name || "" })); }).catch((e) => setError(errMsg(e)));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (f.password !== f.confirm) return setError("As senhas não coincidem");
    setLoading(true); setError("");
    try {
      const r = await api.post("/access/activate", { token, password: f.password, name: f.name, business_name: f.business_name });
      applyAuth(r.data);
      toast.success("Acesso ativado! Vamos configurar seu negócio.");
      nav("/onboarding");
    } catch (err) { setError(errMsg(err)); } finally { setLoading(false); }
  };

  return (
    <AuthShell title="Criar sua senha" subtitle={inv ? <>Sua compra foi confirmada para <span className="font-medium text-foreground" data-testid="activate-email">{inv.email}</span>. Escolha uma senha para entrar no app.</> : "Validando seu link de acesso..."}
      footer={<>Já ativou? <Link to="/login" data-testid="link-login" className="text-primary font-medium hover:underline">Entrar</Link></>}>
      {inv?.status === "activated" ? (
        <div data-testid="activate-done" className="rounded-xl border border-emerald-500/40 bg-emerald-500/10 p-4 text-sm">Este acesso já foi ativado. <Link to="/login" className="text-primary font-medium underline">Faça login</Link> ou use "Esqueci minha senha".</div>
      ) : inv ? (
        <form onSubmit={submit} className="space-y-4" data-testid="activate-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Seu nome"><Input data-testid="activate-name" required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></Field>
            <Field label="Nome do negócio"><Input data-testid="activate-business" value={f.business_name} onChange={(e) => setF({ ...f, business_name: e.target.value })} placeholder="Burger da Maria" /></Field>
          </div>
          <Field label="Senha (mín. 6 caracteres)"><PasswordInput data-testid="activate-password" required minLength={6} value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} /></Field>
          <Field label="Confirmar senha"><PasswordInput data-testid="activate-confirm" required minLength={6} value={f.confirm} onChange={(e) => setF({ ...f, confirm: e.target.value })} /></Field>
          {error && <p data-testid="activate-error" className="text-sm text-rose-500 bg-rose-500/10 rounded-lg px-3 py-2">{error}</p>}
          <Button data-testid="activate-submit" type="submit" className="w-full h-11 rounded-xl font-semibold" disabled={loading}>{loading ? "Ativando..." : "Criar senha e entrar"}</Button>
        </form>
      ) : error ? (
        <p data-testid="activate-error" className="text-sm text-rose-500 bg-rose-500/10 rounded-lg px-3 py-2">{error}</p>
      ) : null}
    </AuthShell>
  );
}

export function AuthCallback() {
  const nav = useNavigate();
  const { applyAuth } = useAuth();
  const done = useRef(false);
  const [error, setError] = useState("");
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const sid = new URLSearchParams(window.location.hash.replace(/^#/, "")).get("session_id");
    api.post("/auth/google/session", { session_id: sid })
      .then((r) => { window.history.replaceState(null, "", window.location.pathname); const u = applyAuth(r.data); nav(u.onboarding_done ? "/" : "/onboarding", { replace: true }); })
      .catch((e) => { window.history.replaceState(null, "", window.location.pathname); setError(errMsg(e)); });
  }, [nav, applyAuth]);
  if (error) return (
    <AuthShell title="Não foi possível entrar" subtitle={error} footer={<Link to="/login" className="text-primary font-medium hover:underline" data-testid="link-login">Voltar para o login</Link>}>
      <p className="text-sm text-muted-foreground">Use o mesmo e-mail da compra ou ative com o código recebido.</p>
    </AuthShell>
  );
  return <div className="min-h-app flex items-center justify-center" data-testid="auth-callback"><div className="w-10 h-10 rounded-full border-4 border-primary/30 border-t-primary animate-spin" /></div>;
}
